/**
 * 历史表按天切段。
 *
 * <p>5 分钟档一屏能滚过好几天，只靠行内那个小日期分辨太吃力。切成段之后日期
 * 只出现一次，时间列就只剩 `14:55` —— 省下来的宽度正好给行尾的验算入口。
 *
 * <p><b>用设备本地时区。</b> 与 {@link ./historyTime historyTime} 同一口径：
 * `country_registry.timezone` 管的是业务日边界，不是给玩家看的时钟。
 */

import { isSameLocalDay } from './historyTime.ts';

export type HistoryDayGroup<T> = {
  /**
   * 组的稳定 key，供 React 用。
   *
   * <p>无时间组是就地断出来的，一次返回里可能有不止一个，所以不能像早前那样
   * 固定给空串 —— 必须每组各自唯一。渲染层仍然只看 {@link at} 是否 `> 0`
   * 来决定画不画分隔条，key 唯一只是为了 React 不认错组件。
   */
  key: string;
  /**
   * 这一组的代表时刻，秒。页面用它格式化分隔条上的日期。
   *
   * <p>`0` 表示这一组的时间不可用 —— 页面应当**不画分隔条**，而不是画一个
   * 1970-01-01。
   */
  at: number;
  rows: T[];
};

/** 一个时刻能不能当成真实时刻。0 是 1970，NaN 是后端没给。 */
function usable(seconds: number): boolean {
  return Number.isFinite(seconds) && seconds > 0;
}

/**
 * 按本地日历日切段。**不重排** —— 接口已按开奖时刻倒序，重排只会和它打架。
 *
 * <p>时间不可用的行就地断组：遇到一行坏时间，就在**当前位置**结束上一组、
 * 开一个新的无时间组，而不是把它挪到数组末尾。挪到末尾会把「同一天但被一行
 * 坏数据隔开」的两段真实日期焊接成一组，破坏本函数自己承诺的不重排。
 * 连续的坏时间行仍然共用同一个无时间组，不会一行一组地碎掉。
 */
export function groupByLocalDay<T extends { drawnAt: number }>(
  rows: readonly T[],
): HistoryDayGroup<T>[] {
  const groups: HistoryDayGroup<T>[] = [];
  let unknownSeq = 0;

  for (const row of rows) {
    const at = Number(row.drawnAt);
    const last = groups[groups.length - 1];
    if (!usable(at)) {
      if (last && last.at === 0) {
        last.rows.push(row);
      } else {
        groups.push({ key: `unknown-${unknownSeq++}`, at: 0, rows: [row] });
      }
      continue;
    }
    if (last && usable(last.at) && isSameLocalDay(last.at, at)) {
      last.rows.push(row);
    } else {
      groups.push({ key: String(at), at, rows: [row] });
    }
  }

  return groups;
}
